/**
 * GrcPlanningManagerPage — planning hebdo de l'équipe communication / GRC.
 *
 * Même format que le planning scolaire : grille 7 jours, création de créneaux,
 * profils réutilisables + génération sur période, grisage vacances/fériés,
 * transmission paie. Couleur du module GRC.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Send, ArrowLeft, Megaphone, GripVertical } from 'lucide-react';
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable,
  type DragEndEvent, DragOverlay,
} from '@dnd-kit/core';
import { api, ApiException } from '@/lib/api';
import { Button, Input, Field, Alert, Badge, LogoMark, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { BoutonCloturePeriode } from '@/components/planning/BoutonCloturePeriode';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { useAuth } from '@/lib/auth-context';
import { AppHeader } from '@/components/AppHeader';
import { PresenceSimplePage } from '@/pages/shared/PresenceSimplePage';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { DeclarerAbsenceModal } from '@/pages/service-technique/DeclarerAbsenceModal';
import { PlanningGrilleUnifiee, type CreneauGrille } from '@/components/planning/PlanningGrilleUnifiee';
import { PlanningVueEquipe } from '@/components/planning/PlanningVueEquipe';
import { CreneauEditModal, type CreneauEditValues } from '@/components/planning/CreneauEditModal';
import { PostesManagerUnifie, type PostesPlanningApi } from '@/components/planning/PostesManagerUnifie';
import { interpretPlanningDrop } from '@/components/planning/planning-drag';
import { useToast } from '@/components/ui/Toast';
import { ThemeToggle } from '@/components/ThemeToggle';
import { GRC_PLANNING_TYPES } from '@roronoa-collectivites/shared-types';
import type { GrcPlanningCreneau, GrcPlanningType, GrcPlanningProfil, ProfilPlage } from '@roronoa-collectivites/shared-types';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const PLAGES = [
  { value: 'matin', label: 'Matin', debut: '08:00', fin: '12:00' },
  { value: 'apres_midi', label: 'Après-midi', debut: '13:30', fin: '17:30' },
  { value: 'journee', label: 'Journée', debut: '08:00', fin: '17:30' },
  { value: 'libre', label: 'Libre', debut: '', fin: '' },
] as const;
type PlageVal = typeof PLAGES[number]['value'];

function startOfWeek(d = new Date()): Date {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const dow = x.getDay() === 0 ? 7 : x.getDay();
  x.setDate(x.getDate() - (dow - 1)); return x;
}
function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function ymd(d: Date): string { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function hhmm(iso: string): string { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }

export function GrcPlanningManagerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // 24/07 — l'agent GRC obtient la MÊME vue que toutes les autres briques
  // (bascule Équipe / Individuelle sur la grille partagée), au lieu de sa liste
  // de cartes propriétaire. Le composant gère lui-même les droits : l'agent
  // consulte, l'encadrement édite.
  if (user?.role === 'agent') {
    return <GrcAgentPlanningView />;
  }
  const { confirm, dialogElement } = useConfirmDialog();
  const { toast } = useToast();
  // Planning UNIFIÉ (20/07) : présence = sous-vue de cette page.
  const [absModal, setAbsModal] = useState<{ agent: { id: string; fullName: string }; jourISO: string } | null>(null);
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek());
  const [creneaux, setCreneaux] = useState<GrcPlanningCreneau[]>([]);
  const [agents, setAgents] = useState<{ id: string; fullName: string; role: string; couleurMarker: string | null }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [presetDay, setPresetDay] = useState<Date | null>(null);
  const [agentId, setAgentId] = useState('');
  const [titre, setTitre] = useState('');
  const [type, setType] = useState<GrcPlanningType>('evenement');
  const [plage, setPlage] = useState<PlageVal>('matin');
  const [heureDebut, setHeureDebut] = useState('08:00');
  const [heureFin, setHeureFin] = useState('12:00');
  const [jour, setJour] = useState('');
  const [saving, setSaving] = useState(false);
  const [nonTravailles, setNonTravailles] = useState<Map<string, string>>(new Map());
  const [profilsOpen, setProfilsOpen] = useState(false);
  const [postesOpen, setPostesOpen] = useState(false);
  // Postes glissables (chips) + drag en cours
  const [postes, setPostes] = useState<Array<{ id: string; titre: string; dureeMin: number; type: string; couleur: string | null }>>([]);
  const [dragPoste, setDragPoste] = useState<{ id: string; titre: string; dureeMin: number; couleur: string | null } | null>(null);
  // affectation depuis drop d'un poste
  const [affectPoste, setAffectPoste] = useState<{ poste: { id: string; titre: string; dureeMin: number; couleur: string | null }; jour: Date } | null>(null);
  // Déclaration d'absence catégorisée (maladie, congé, justifiée/injustifiée…)
  const [absDecl, setAbsDecl] = useState<{ agent: { id: string; fullName: string }; jourISO: string } | null>(null);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  async function load() {
    setLoading(true);
    try {
      const from = ymd(weekStart);
      const to = ymd(addDays(weekStart, 7));
      const [week, ag, jnt, pst] = await Promise.all([
        api.grc.planning.getWeek(from, to),
        api.grc.planning.listAgents(),
        api.grc.planning.joursNonTravailles(from, to).catch(() => []),
        api.grc.planning.listPostes().catch(() => []),
      ]);
      setCreneaux(week); setAgents(ag.items);
      setNonTravailles(new Map(jnt.map((j) => [j.date, j.libelle])));
      setPostes(pst);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [weekStart]);

  function choisirPlage(p: PlageVal) {
    setPlage(p);
    const def = PLAGES.find((x) => x.value === p);
    if (def && def.debut) { setHeureDebut(def.debut); setHeureFin(def.fin); }
  }
  function ouvrirCreation(day: Date) {
    setEditId(null);
    setPresetDay(day); setJour(ymd(day));
    setAgentId(''); setTitre(''); setType('evenement'); choisirPlage('matin');
    setDialog(true);
  }
  function ouvrirEdition(c: GrcPlanningCreneau) {
    setEditId(c.id);
    const deb = new Date(c.dateDebut);
    const fin = new Date(c.dateFin);
    setPresetDay(deb); setJour(ymd(deb));
    setAgentId(c.agentUserId); setTitre(c.titre); setType(c.type);
    setPlage(c.plageType ?? 'libre');
    setHeureDebut(`${String(deb.getHours()).padStart(2, '0')}:${String(deb.getMinutes()).padStart(2, '0')}`);
    setHeureFin(`${String(fin.getHours()).padStart(2, '0')}:${String(fin.getMinutes()).padStart(2, '0')}`);
    setDialog(true);
  }
  async function createCreneau() {
    if (!agentId || !titre.trim() || !jour || !heureDebut || !heureFin) return;
    setSaving(true); setError(null);
    try {
      const dateDebut = new Date(`${jour}T${heureDebut}:00`).toISOString();
      const dateFin = new Date(`${jour}T${heureFin}:00`).toISOString();
      if (editId) {
        await api.grc.planning.updateCreneau(editId, { agentUserId: agentId, dateDebut, dateFin, titre: titre.trim(), type, plageType: plage });
      } else {
        await api.grc.planning.createCreneau({ agentUserId: agentId, dateDebut, dateFin, titre: titre.trim(), type, plageType: plage });
      }
      setDialog(false); setEditId(null);
      await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  }
  async function supprimer(c: GrcPlanningCreneau) {
    const ok = await confirm({ title: 'Supprimer ce créneau ?', description: `${c.titre} — ${c.agentNom ?? ''}`, confirmLabel: 'Supprimer', danger: true });
    if (!ok) return;
    try { await api.grc.planning.deleteCreneau(c.id); await load(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de la suppression'); }
  }
  async function basculerAbsent(c: GrcPlanningCreneau) {
    try {
      const updated = await api.grc.planning.updateStatut(c.id, c.statut === 'absent' ? 'planifie' : 'absent');
      setCreneaux((prev) => prev.map((x) => x.id === c.id ? updated : x));
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function transmettrePaie() {
    const ok = await confirm({
      title: 'Transmettre cette semaine à la paie ?',
      description: `Semaine du ${weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}. Les créneaux seront marqués comme transmis.`,
      confirmLabel: 'Transmettre',
    });
    if (!ok) return;
    try {
      const res = await api.grc.planning.transmettrePaie(ymd(weekStart), ymd(addDays(weekStart, 7)));
      await load(); setError(null);
      await confirm({ title: 'Transmis', description: `${res.transmis} créneau(x) transmis à la paie.`, confirmLabel: 'OK', cancelLabel: '' });
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de la transmission'); }
  }

  // Drag-n-drop : déplacer un créneau existant OU glisser un poste sur la grille.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  function onDragEnd(event: DragEndEvent) {
    setDragPoste(null);
    const res = interpretPlanningDrop<{ id: string; titre: string; dureeMin: number; couleur: string | null }>(event, weekDays);
    if (!res) return;
    if (res.kind === 'move') {
      // Re-déplacement d'un créneau posé : on met à jour début/fin (durée conservée).
      void (async () => {
        try {
          await api.grc.planning.updateCreneau(res.creneau.id, {
            dateDebut: res.nouveauDebut.toISOString(),
            dateFin: res.nouvelleFin.toISOString(),
          });
          await load();
        } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors du déplacement'); }
      })();
      return;
    }
    // Dépose d'un poste. En vue individuelle, l'agent est déjà choisi → affectation
    // directe (pas de re-demande de la personne). Sinon, on ouvre la modale.
    if (vue === 'individuelle' && agentIndividuel) {
      const fin = new Date(res.jour.getTime() + res.poste.dureeMin * 60000);
      void (async () => {
        try {
          await api.grc.planning.createCreneau({
            agentUserId: agentIndividuel,
            dateDebut: res.jour.toISOString(),
            dateFin: fin.toISOString(),
            titre: res.poste.titre,
            type: 'accueil_public',
            plageType: 'libre',
            couleurMarker: res.poste.couleur ?? undefined,
          });
          await load();
        } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de l\'affectation'); }
      })();
      return;
    }
    setAffectPoste({ poste: res.poste, jour: res.jour });
  }

  // Mapping des créneaux GRC vers la forme générique de la grille partagée.
  const creneauxGrille: CreneauGrille[] = creneaux.map((c) => ({
    id: c.id,
    agentUserId: c.agentUserId,
    agentFullName: c.agentNom ?? '—',
    agentCouleurMarker: null,
    dateDebut: c.dateDebut,
    dateFin: c.dateFin,
    titre: c.titre,
    description: c.description,
    signalementId: null,
    signalementNumero: null,
    type: c.type,
    couleurMarker: c.couleurMarker,
  }));
  const days = weekDays.map((d) => ({ label: JOURS[d.getDay() === 0 ? 6 : d.getDay() - 1]!, date: d }));
  const todayKey = new Date().toDateString();
  const colorAgentById = new Map(agents.map((a) => [a.id, a.couleurMarker ?? null]));
  const colorOfCreneauGrc = (c: CreneauGrille): string => {
    const orig = creneaux.find((x) => x.id === c.id);
    if (c.type === 'absent' || orig?.statut === 'absent') return '#b91c1c';
    // Priorité : couleur du créneau (poste) → couleur de l'agent → défaut.
    return c.couleurMarker ?? colorAgentById.get(c.agentUserId) ?? '#0ea5e9';
  };

  // ── Refonte planning 21/07 : vue Équipe (défaut) / Individuelle + modale unifiée
  const [vue, setVue] = useState<'equipe' | 'individuelle'>('equipe');
  const [agentIndividuel, setAgentIndividuel] = useState<string>('');
  const [editCreneau, setEditCreneau] = useState<GrcPlanningCreneau | null>(null);

  // Vue individuelle : par défaut le 1er agent (on ne dépend pas de « qui regarde »).
  useEffect(() => {
    if (!agentIndividuel && agents.length > 0) setAgentIndividuel(agents[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents]);

  // Refonte 21/07 : la liste « Type » de la modale d'édition vient des POSTES
  // réellement créés (plus de liste prédéfinie que personne n'a créée). Chaque
  // poste porte un type GRC + une couleur ; on affiche le titre du poste.
  const typesGrc = postes.map((p) => ({ value: p.type, label: p.titre }));
  const agentsVueEquipe = agents.map((a) => ({ id: a.id, fullName: a.fullName, couleurMarker: a.couleurMarker ?? null }));

  async function setCouleurAgent(agentId: string, couleur: string | null) {
    try { await api.grc.planning.setAgentCouleur(agentId, couleur); await load(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur couleur'); }
  }

  async function submitEdit(v: CreneauEditValues) {
    if (!editCreneau) return;
    // Le back GRC valide description en string optionnel (pas null) → on omet si vide.
    // Le "type" provient d'un poste choisi (voir typesGrc) ; on récupère sa couleur.
    const posteChoisi = postes.find((p) => p.type === v.type);
    const couleur6 = posteChoisi?.couleur && /^#[0-9A-Fa-f]{6}$/.test(posteChoisi.couleur) ? posteChoisi.couleur : undefined;
    await api.grc.planning.updateCreneau(editCreneau.id, {
      agentUserId: v.agentUserId, dateDebut: v.dateDebut, dateFin: v.dateFin,
      titre: v.titre, type: v.type as typeof editCreneau.type,
      description: v.description ?? undefined,
      couleurMarker: couleur6,
    });
    setEditCreneau(null);
    await load();
  }
  async function deleteEdit() {
    if (!editCreneau) return;
    const ok = await confirm({ title: 'Supprimer ce créneau ?', description: `${editCreneau.titre} — ${editCreneau.agentNom ?? ''}`, confirmLabel: 'Supprimer', danger: true });
    if (!ok) return;
    await api.grc.planning.deleteCreneau(editCreneau.id);
    setEditCreneau(null);
    await load();
  }

  const postesApiGrc: PostesPlanningApi = {
    listPostes: () => api.grc.planning.listPostes().then((ps) => ps.map((p) => ({ id: p.id, titre: p.titre, dureeMin: p.dureeMin, couleur: p.couleur }))),
    createPoste: (b) => api.grc.planning.createPoste(b).then((p) => ({ id: p.id, titre: p.titre, dureeMin: p.dureeMin, couleur: p.couleur })),
    deletePoste: (id) => api.grc.planning.deletePoste(id),
    createCreneau: (b) => api.grc.planning.createCreneau({ agentUserId: b.agentUserId, dateDebut: b.dateDebut, dateFin: b.dateFin, titre: b.titre, type: 'accueil_public', plageType: 'libre', couleurMarker: b.couleurMarker ?? undefined }),
  };

  return (
    <GrcLayout active="planning">
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {/* Sélecteur Équipe / Individuelle (service bloqué = GRC) */}
          <div className="inline-flex rounded-r-lg border border-border-hi bg-surface/60 p-0.5">
            <button type="button" onClick={() => setVue('equipe')} className={`rounded-r-md px-3 py-1.5 text-sm ${vue === 'equipe' ? 'bg-grc text-white' : 'text-ink-muted'}`}>Équipe</button>
            <button type="button" onClick={() => setVue('individuelle')} className={`rounded-r-md px-3 py-1.5 text-sm ${vue === 'individuelle' ? 'bg-grc text-white' : 'text-ink-muted'}`}>Individuelle</button>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setPostesOpen(true)}>Postes</Button>
            <Button variant="secondary" size="sm" onClick={() => setProfilsOpen(true)}>Profils &amp; génération</Button>
            <Button variant="secondary" size="sm" onClick={() => void transmettrePaie()}>
              <Send className="h-4 w-4" aria-hidden="true" /><span className="hidden sm:inline">Transmettre à la paie</span>
            </Button>
            <BoutonCloturePeriode service="grc" />
          </div>
        </div>
        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="mb-4 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, -7)))}>← Semaine préc.</Button>
          <span className="text-sm font-medium text-ink">Semaine du {weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, 7)))}>Semaine suiv. →</Button>
        </div>

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : vue === 'equipe' ? (
          /* VUE ÉQUIPE (défaut) — 1 ligne/agent, cadres colorés alignés */
          <PlanningVueEquipe
            days={days}
            agents={agentsVueEquipe}
            creneaux={creneauxGrille}
            colorOfCreneau={colorOfCreneauGrc}
            onSetAgentColor={setCouleurAgent}
            onCreneauClick={(c) => { const orig = creneaux.find((x) => x.id === c.id); if (orig) setEditCreneau(orig); }}
            /* LOT 2 — déclencheur du modal d'absence depuis la grille. */
            onDeclarerAbsence={(agent, jourISO) =>
              setAbsModal({ agent: { id: agent.id, fullName: agent.fullName }, jourISO })}
          />
        ) : (
          /* VUE INDIVIDUELLE — grille horaire d'UN agent (glisser des postes pour créer) */
          <DndContext sensors={sensors} onDragStart={(e) => { const d = e.active.data.current as { poste?: typeof postes[number] } | undefined; setDragPoste(d?.poste ?? null); }} onDragEnd={onDragEnd}>
            {/* Sélecteur d'agent : on regarde le détail de N'IMPORTE quel agent */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <label className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Agent :</label>
              <select value={agentIndividuel} onChange={(e) => setAgentIndividuel(e.target.value)} className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                {agents.map((a) => <option key={a.id} value={a.id}>{a.fullName}</option>)}
              </select>
            </div>
            {/* Barre de postes glissables */}
            {postes.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-r-lg border border-border bg-surface/30 p-2.5">
                <span className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Postes à glisser :</span>
                {postes.map((p) => <PosteChip key={p.id} poste={p} />)}
                <button type="button" onClick={() => setPostesOpen(true)} className="ml-auto text-xs text-grc-fg hover:underline">Gérer</button>
              </div>
            )}
            {/* Grille de planning UNIQUE — filtrée sur l'agent choisi (accent grc) */}
            <PlanningGrilleUnifiee
              accent="grc"
              days={days}
              todayKey={todayKey}
              creneaux={creneauxGrille}
              filterAgentId={agentIndividuel}
              onCreneauClick={(c) => { const orig = creneaux.find((x) => x.id === c.id); if (orig) setEditCreneau(orig); }}
              colorOfCreneau={colorOfCreneauGrc}
              nonTravailles={nonTravailles}
            />
            <DragOverlay>
              {dragPoste && (
                <div className="rounded-r-pill border border-grc/40 bg-grc-tint/90 px-3 py-1.5 text-xs font-semibold text-grc-fg shadow-pop">
                  {dragPoste.titre}
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </main>

      {/* Déclaration d'absence depuis la sous-vue Présence */}
      {absModal && (
        <DeclarerAbsenceModal
          agent={absModal.agent}
          jourISO={absModal.jourISO}
          service="grc"
          onClose={() => setAbsModal(null)}
          onDone={(conflits) => {
            setAbsModal(null);
            void load();
            toast.success(conflits > 0
              ? `Absence enregistrée. ${conflits} tâche(s) à réaffecter — responsable alerté.`
              : 'Absence enregistrée.');
          }}
        />
      )}

      {/* Modale d'édition unifiée (avec Supprimer) — partagée par toutes les briques */}
      {editCreneau && (
        <CreneauEditModal
          mode="edit"
          accent="grc"
          initial={{
            id: editCreneau.id, agentUserId: editCreneau.agentUserId, titre: editCreneau.titre,
            description: editCreneau.description, type: editCreneau.type,
            dateDebut: editCreneau.dateDebut, dateFin: editCreneau.dateFin,
          }}
          agents={agents}
          types={typesGrc}
          onClose={() => setEditCreneau(null)}
          onSubmit={submitEdit}
          onDelete={deleteEdit}
        />
      )}
      {dialogElement}
      <ProfilsDialogGrc open={profilsOpen} onClose={() => setProfilsOpen(false)} agents={agents} onGenerated={() => { setProfilsOpen(false); void load(); }} />
      {/* Modale Postes unifiée (Affecter + créer/supprimer) — « Nouveau poste »/« Gérer » */}
      <PostesManagerUnifie
        open={postesOpen}
        onClose={() => setPostesOpen(false)}
        agents={agents}
        planningApi={postesApiGrc}
        accent="grc"
        typeCreneau="accueil_public"
        titre="Postes GRC"
        onChanged={() => { void load(); }}
      />

      {/* Affectation d'un poste déposé sur un jour */}
      {affectPoste && (
        <AffectPosteDialog
          poste={affectPoste.poste}
          jour={affectPoste.jour}
          agents={agents}
          onClose={() => setAffectPoste(null)}
          onDone={() => { setAffectPoste(null); void load(); }}
        />
      )}

      {/* Déclaration d'absence catégorisée (maladie, congé, justifiée…) */}
      {absDecl && (
        <DeclarerAbsenceModal
          agent={absDecl.agent}
          jourISO={absDecl.jourISO}
          service="grc"
          onClose={() => setAbsDecl(null)}
          onDone={() => { setAbsDecl(null); void load(); }}
        />
      )}
    </GrcLayout>
  );
}

// ============================================================================
function ProfilsDialogGrc({ open, onClose, agents, onGenerated }: {
  open: boolean; onClose: () => void;
  agents: { id: string; fullName: string; role: string }[];
  onGenerated: () => void;
}) {
  const JOURS_SEM = [{ n: 1, l: 'Lun' }, { n: 2, l: 'Mar' }, { n: 3, l: 'Mer' }, { n: 4, l: 'Jeu' }, { n: 5, l: 'Ven' }, { n: 6, l: 'Sam' }, { n: 7, l: 'Dim' }];
  const [profils, setProfils] = useState<GrcPlanningProfil[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editProfilId, setEditProfilId] = useState<string | null>(null);
  const [nom, setNom] = useState('');
  const [type, setType] = useState<GrcPlanningType>('evenement');
  const [plages, setPlages] = useState<ProfilPlage[]>([]);
  const [pJour, setPJour] = useState(1);
  const [pDebut, setPDebut] = useState('09:00');
  const [pFin, setPFin] = useState('12:00');

  const [profilId, setProfilId] = useState('');
  const [agentSel, setAgentSel] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [inclureVacances, setInclureVacances] = useState(false);

  async function loadProfils() {
    try { setProfils(await api.grc.planning.listProfils()); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  useEffect(() => { if (open) void loadProfils(); }, [open]);

  function ajouterPlage() { setPlages((prev) => [...prev, { jour: pJour, debut: pDebut, fin: pFin }]); }
  async function creerProfil() {
    if (!nom.trim() || plages.length === 0) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      if (editProfilId) {
        const r = await api.grc.planning.updateProfil(editProfilId, { nom: nom.trim(), type, plages });
        setMsg(`Profil modifié. ${r.crees} créneau(x) futur(s) régénéré(s)${r.supprimes ? `, ${r.supprimes} remplacé(s)` : ''}.`);
      } else {
        await api.grc.planning.createProfil({ nom: nom.trim(), type, plages });
      }
      setEditProfilId(null); setNom(''); setPlages([]); await loadProfils(); onGenerated();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }
  function editerProfil(p: GrcPlanningProfil) {
    setEditProfilId(p.id); setNom(p.nom); setType(p.type); setPlages(p.plages);
  }
  function annulerEdition() { setEditProfilId(null); setNom(''); setPlages([]); }
  async function supprimerProfil(id: string) {
    try { await api.grc.planning.deleteProfil(id); await loadProfils(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function generer() {
    if (!profilId || agentSel.length === 0 || !from || !to) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await api.grc.planning.generer({ profilId, agentIds: agentSel, from, to, inclureVacances });
      setMsg(`${r.creneauxCrees} créneau(x) créé(s)${r.joursIgnores > 0 ? `, ${r.joursIgnores} jour(s) non travaillé(s) ignoré(s)` : ''}.`);
      onGenerated();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de la génération'); }
    finally { setBusy(false); }
  }

  const selStyle = 'h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg';

  return (
    <Dialog open={open} onClose={onClose} title="Profils de planning & génération" size="lg"
      footer={<Button variant="ghost" onClick={onClose}>Fermer</Button>}>
      <div className="space-y-6">
        {error && <Alert variant="error">{error}</Alert>}

        <section>
          <h3 className="mb-2 text-sm font-semibold text-ink">{editProfilId ? 'Modifier le profil' : 'Créer un profil'}</h3>
          {editProfilId && <p className="mb-2 text-xs text-ink-muted">Les créneaux futurs (non transmis à la paie) générés depuis ce profil seront régénérés avec les nouvelles plages.</p>}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nom du profil" htmlFor="pf-nom" required>
                <Input id="pf-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Permanence réseaux sociaux" />
              </Field>
              <Field label="Type" htmlFor="pf-type">
                <select id="pf-type" value={type} onChange={(e) => setType(e.target.value as GrcPlanningType)} className={selStyle}>
                  {GRC_PLANNING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="flex items-end gap-2">
              <Field label="Jour" htmlFor="pf-jour">
                <select id="pf-jour" value={pJour} onChange={(e) => setPJour(Number(e.target.value))} className={selStyle}>
                  {JOURS_SEM.map((j) => <option key={j.n} value={j.n}>{j.l}</option>)}
                </select>
              </Field>
              <Field label="Début" htmlFor="pf-debut"><Input id="pf-debut" type="time" value={pDebut} onChange={(e) => setPDebut(e.target.value)} /></Field>
              <Field label="Fin" htmlFor="pf-fin"><Input id="pf-fin" type="time" value={pFin} onChange={(e) => setPFin(e.target.value)} /></Field>
              <Button variant="secondary" onClick={ajouterPlage}><Plus className="h-4 w-4" /> Plage</Button>
            </div>
            {plages.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {plages.map((pl, i) => (
                  <li key={i} className="inline-flex items-center gap-1 rounded-r-pill border border-border px-2.5 py-1 text-xs text-ink">
                    {JOURS_SEM.find((j) => j.n === pl.jour)?.l} {pl.debut}–{pl.fin}
                    <button type="button" onClick={() => setPlages((prev) => prev.filter((_, idx) => idx !== i))} className="text-ink-subtle hover:text-danger">×</button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <Button variant="grc" loading={busy} onClick={creerProfil} disabled={!nom.trim() || plages.length === 0}>{editProfilId ? 'Enregistrer les modifications' : 'Enregistrer le profil'}</Button>
              {editProfilId && <Button variant="ghost" onClick={annulerEdition}>Annuler</Button>}
            </div>
          </div>
        </section>

        {profils.length > 0 && (
          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink">Profils existants</h3>
            <ul className="space-y-1.5">
              {profils.map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded-r-md border border-border bg-surface/40 px-3 py-2">
                  <span className="text-sm text-ink">{p.nom} <span className="text-xs text-ink-subtle">· {p.plages.length} plage(s)</span></span>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => editerProfil(p)} className="text-xs text-ink-subtle hover:text-grc-fg">Modifier</button>
                    <button type="button" onClick={() => supprimerProfil(p.id)} className="text-xs text-ink-subtle hover:text-danger">Suppr.</button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <h3 className="mb-2 text-sm font-semibold text-ink">Générer sur une période</h3>
          {msg && <Alert variant="success" className="mb-2">{msg}</Alert>}
          <div className="space-y-3">
            <Field label="Profil" htmlFor="gn-profil" required>
              <select id="gn-profil" value={profilId} onChange={(e) => setProfilId(e.target.value)} className={selStyle}>
                <option value="">— Choisir —</option>
                {profils.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
            </Field>
            <div>
              <span className="mb-1.5 block text-sm font-medium text-ink">Agents</span>
              <div className="flex flex-wrap gap-1.5">
                {agents.map((a) => {
                  const on = agentSel.includes(a.id);
                  return (
                    <button key={a.id} type="button"
                      onClick={() => setAgentSel((prev) => on ? prev.filter((x) => x !== a.id) : [...prev, a.id])}
                      className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all', on ? 'bg-grc text-white' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                      {a.fullName}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Du" htmlFor="gn-from" required><Input id="gn-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
              <Field label="Au" htmlFor="gn-to" required><Input id="gn-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
            </div>
            <label className="flex items-center gap-1.5 text-sm text-ink-muted">
              <input type="checkbox" checked={inclureVacances} onChange={(e) => setInclureVacances(e.target.checked)} />
              Générer aussi pendant les vacances/fériés
            </label>
            <Button variant="grc" loading={busy} onClick={generer} disabled={!profilId || agentSel.length === 0 || !from || !to}>Générer le planning</Button>
          </div>
        </section>
      </div>
    </Dialog>
  );
}

/**
 * Vue AGENT du planning GRC — lecture seule de SES créneaux de la semaine.
 * Même principe que MonPlanningScolairePage / MaTournee technique : aucune
 * action de gestion (pas de création, pas de profils, pas de génération).
 */
/**
 * GrcAgentPlanningView — vue planning de l'AGENT communication.
 *
 * 24/07 — utilise le composant PARTAGÉ `PresenceSimplePage`, identique à
 * l'urbanisme, la comptabilité, le scolaire et la petite enfance. Remplace
 * l'ancienne liste de cartes propriétaire (`MonPlanningGrcView`), qui donnait
 * au même planning un rendu différent selon la brique.
 *
 * L'API GRC nomme `maSemaine` ce que le contrat commun appelle `getMaSemaine`,
 * et son créneau ne porte pas `profilId` : on comble les deux ici.
 */
function GrcAgentPlanningView() {
  const p = api.grc.planning;
  // Le GRC a quatre statuts (planifie / en_cours / termine / absent) là où le
  // contrat commun n'en connaît que deux. Seul « absent » change le rendu (le
  // créneau passe en rouge) : tout le reste est un créneau planifié.
  const versGrille = (c: GrcPlanningCreneau) => ({
    ...c,
    profilId: null,
    statut: (c.statut === 'absent' ? 'absent' : 'planifie') as 'planifie' | 'absent',
  });
  return (
    <GrcLayout active="planning">
      <PresenceSimplePage
        planningApi={{
          ...p,
          getWeek: (from: string, to: string) => p.getWeek(from, to).then((cs) => cs.map(versGrille)),
          getMaSemaine: (from: string, to: string) => p.maSemaine(from, to).then((cs) => cs.map(versGrille)),
          // Créneaux et profils GRC imposent un `type`, optionnel dans le
          // contrat commun. Valeur par défaut : « evenement », le type courant
          // du service communication.
          createCreneau: (body) =>
            p.createCreneau({
              agentUserId: body.agentUserId,
              dateDebut: body.dateDebut,
              dateFin: body.dateFin,
              titre: body.titre,
              description: body.description,
              type: (body.type ?? 'evenement') as GrcPlanningType,
              couleurMarker: body.couleurMarker ?? undefined,
            }).then(versGrille),
          updateCreneau: (id, body) => p.updateCreneau(id, body).then(versGrille),
          createProfil: (body: { nom: string; type?: string; plages: ProfilPlage[] }) =>
            p.createProfil({
              nom: body.nom,
              type: (body.type ?? 'evenement') as GrcPlanningType,
              plages: body.plages,
            }),
        }}
        accent="grc"
        service="grc"
      />
    </GrcLayout>
  );
}

const JOURS_GRC = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
function MonPlanningGrcView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek());
  const [creneaux, setCreneaux] = useState<GrcPlanningCreneau[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  async function load() {
    setLoading(true);
    try {
      setCreneaux(await api.grc.planning.maSemaine(ymd(weekStart), ymd(addDays(weekStart, 7))));
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [weekStart]);

  const parJour = (day: Date) => creneaux
    .filter((c) => ymd(new Date(c.dateDebut)) === ymd(day))
    .sort((a, b) => a.dateDebut.localeCompare(b.dateDebut));

  return (
    <GrcLayout active="planning">
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="mb-5">
          <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">Mon planning</h1>
          <p className="mt-1 text-sm text-ink-muted">Vos affectations de la semaine.</p>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="mb-4 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, -7)))}>← Préc.</Button>
          <span className="text-sm font-medium text-ink">Semaine du {weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}</span>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, 7)))}>Suiv. →</Button>
        </div>

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : creneaux.length === 0 ? (
          <div className="rounded-r-2xl border border-border bg-surface/40 p-6"><p className="text-sm text-ink-muted">Aucune affectation cette semaine.</p></div>
        ) : (
          <div className="space-y-3">
            {weekDays.map((day, idx) => {
              const items = parJour(day);
              if (items.length === 0) return null;
              const isToday = ymd(day) === ymd(new Date());
              return (
                <div key={ymd(day)}>
                  <div className={['mb-1.5 text-sm font-semibold', isToday ? 'text-grc' : 'text-ink'].join(' ')}>
                    {JOURS_GRC[idx]} {day.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                  </div>
                  <div className="space-y-2">
                    {items.map((c) => (
                      <div key={c.id} className="flex items-center gap-3 rounded-r-2xl border border-border bg-surface/40 px-4 py-3">
                        <Megaphone className="h-5 w-5 shrink-0 text-grc" aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-ink">{hhmm(c.dateDebut)}–{hhmm(c.dateFin)}</span>
                            <Badge>{GRC_PLANNING_TYPES.find((t) => t.value === c.type)?.label ?? c.type}</Badge>
                            {c.statut === 'absent' && <span className="text-xs font-medium text-danger">Absence signalée</span>}
                          </div>
                          <p className="text-xs text-ink-muted">{c.titre}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </GrcLayout>
  );
}

// ============================================================================
// PosteChip — poste glissable ; DayDrop — colonne-jour droppable
// ============================================================================
function PosteChip({ poste }: { poste: { id: string; titre: string; dureeMin: number; couleur: string | null } }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `poste-${poste.id}`, data: { poste } });
  return (
    <button ref={setNodeRef} {...listeners} {...attributes} type="button"
      className="inline-flex cursor-grab items-center gap-1 rounded-r-pill border border-grc/30 bg-grc-tint/60 px-2.5 py-1 text-xs font-semibold text-grc-fg active:cursor-grabbing"
      style={{ opacity: isDragging ? 0.4 : 1 }}>
      <GripVertical className="h-3 w-3 opacity-60" />
      {poste.titre}
    </button>
  );
}

// ============================================================================
// AffectPosteDialog — affecter un poste déposé (personne + heure), jour pré-rempli
// ============================================================================
function AffectPosteDialog({ poste, jour, agents, onClose, onDone }: {
  poste: { id: string; titre: string; dureeMin: number; couleur: string | null };
  jour: Date;
  agents: { id: string; fullName: string; role: string }[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [agentId, setAgentId] = useState('');
  const [heure, setHeure] = useState('09:00');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function affecter() {
    if (!agentId) { setError('Choisir une personne'); return; }
    setSaving(true); setError(null);
    const [h, m] = heure.split(':').map(Number);
    const debut = new Date(jour);
    debut.setHours(h ?? 9, m ?? 0, 0, 0);
    const fin = new Date(debut.getTime() + poste.dureeMin * 60000);
    try {
      await api.grc.planning.createCreneau({
        agentUserId: agentId,
        dateDebut: debut.toISOString(),
        dateFin: fin.toISOString(),
        titre: poste.titre,
        type: 'accueil_public',
        plageType: 'libre',
        couleurMarker: poste.couleur ?? undefined,
      });
      onDone();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open onClose={onClose} title={`Affecter « ${poste.titre} »`} size="sm">
      <div className="flex flex-col gap-3.5">
        <p className="text-xs text-ink-muted">{jour.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} · durée {Math.round(poste.dureeMin / 60 * 10) / 10} h</p>
        {error && <Alert variant="error">{error}</Alert>}
        <Field label="Personne" htmlFor="ap-agent" required>
          <select id="ap-agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} className="h-11 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink">
            <option value="">—</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.fullName}</option>)}
          </select>
        </Field>
        <Field label="Heure de début" htmlFor="ap-heure">
          <input id="ap-heure" type="time" value={heure} onChange={(e) => setHeure(e.target.value)} className="h-11 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink" />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="grc" loading={saving} onClick={() => void affecter()}>Affecter</Button>
        </div>
      </div>
    </Dialog>
  );
}
